import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@material-ui/core";
import { format } from "date-fns";
import React from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { APIError } from "../../../components/APIError";
import { CustomDialog } from "../../../components/CustomDialog";
import { DialogLoader } from "../../../components/Loader";
import { useSessionStorage } from "../../../hooks";
import { CloseIcon } from "../../../icons";
import { WrapNotesForm, WrapNotesFormValues } from "./WrapNotesForm";

export interface WrapNotesDialogProps {
  isLoading: boolean;
  isSaving: boolean;
  startTime: number;
  fetchErrors: any[];
  saveError: any;
  accountDetails: any;
  wrapNotesTypes: any;
  onCancel: () => void;
  onSubmit: (data: Record<string, any>) => void;
}
export const WrapNotesDialog: React.FC<WrapNotesDialogProps> = ({
  isLoading,
  isSaving,
  startTime,
  fetchErrors,
  saveError,
  accountDetails,
  wrapNotesTypes,
  onCancel,
  onSubmit,
}) => {
  const [defaultValues, persistFormValues] = useSessionStorage(
    "forms/wrapnotes",
    {},
    { removeOnUnmount: true },
  );

  const formContext = useForm<WrapNotesFormValues>({
    defaultValues,
  });

  const { handleSubmit } = formContext;

  return (
    <CustomDialog
      onClose={onCancel}
      data-testid="WrapNotesDialog"
      scroll="paper"
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit(onSubmit),
        // @ts-ignore
        noValidate: true,
        "data-testid": "WrapNotesDialog__Form",
      }}
    >
      <DialogTitle>
        <Title onCancel={onCancel} />
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <DialogLoader />}
        {fetchErrors.length > 0 && (
          <APIError
            messages={fetchErrors.map((error) => error?.message)}
            title="An error has occurred"
            my={0}
            mb={2}
          />
        )}
        {saveError && (
          <APIError
            title="There was an error submitting your wrap-up notes."
            my={0}
            mb={2}
          />
        )}
        {accountDetails && (
          <>
            <Helmet>
              <title>{`${accountDetails.number} - Account Wrap Up`}</title>
            </Helmet>
            <Box mb={1.5}>
              <Typography variant="h5">
                <span data-testid="WrapNotesDialog__Info--customerName">
                  {accountDetails.customerName}
                </span>
                <span data-testid="WrapNotesDialog__Info--accountNumber">
                  {" "}
                  ({accountDetails.number})
                </span>
              </Typography>
            </Box>
            <Box mb={3}>
              <Typography variant="body1">
                {format(startTime, "eee, dd MMM yyyy ppp")}
              </Typography>
            </Box>
          </>
        )}
        {wrapNotesTypes && accountDetails && (
          <WrapNotesForm
            data-testid="WrapNotesForm"
            formContext={formContext}
            onPersist={persistFormValues}
            wrapNotesTypes={wrapNotesTypes}
          />
        )}
      </DialogContent>
      {wrapNotesTypes && accountDetails && (
        <DialogActions>
          <Button color="primary" size="large" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            disableElevation
            type="submit"
            size="large"
            variant="contained"
            color="primary"
            disabled={isSaving}
            data-testid="WrapNotesForm__Button--submit"
          >
            Save
          </Button>
        </DialogActions>
      )}
    </CustomDialog>
  );
};

const Title: React.FC<{
  onCancel: () => void;
}> = ({ onCancel }) => {
  const theme = useTheme();
  return (
    <Box color="text.primary">
      <Typography variant="h6">Wrap-up notes</Typography>
      <Box
        position="absolute"
        mt="-2px"
        right={theme.spacing(2)}
        top={theme.spacing(1)}
      >
        <IconButton
          onClick={onCancel}
          data-testid="WrapNotesDialog__Title--CloseButton"
        >
          <CloseIcon fill={theme.palette.text.primary} />
        </IconButton>
      </Box>
    </Box>
  );
};
